import { useEffect, useMemo, useState } from 'react';
import { Download } from 'lucide-react';
import { Button, Card, Pill } from '@nako/ui';
import {
  buildBalanceSheet,
  buildProfitLoss,
  buildTrialBalance,
  formatMoney,
  getRegion,
} from '@nako/core';
import type { Account, JournalEntry, JournalLine } from '@nako/types';
import { useAuthStore } from '@/stores/auth';
import { useRegionStore } from '@/stores/region';
import { useUiStore } from '@/stores/ui';
import { supabase } from '@/lib/supabase';
import { makeT } from '@/lib/i18n';

function q(v: string | number | boolean | null | undefined): string {
  const s = v == null ? '' : String(v);
  return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
}

function downloadCsv(filename: string, csv: string) {
  const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  setTimeout(() => URL.revokeObjectURL(url), 1000);
}

interface Bundle {
  accounts: Account[];
  entries: JournalEntry[];
  lines: JournalLine[];
}

type Tab = 'tb' | 'pnl' | 'bs';

export default function Reports() {
  const business = useAuthStore((s) => s.business)!;
  const lang = useUiStore((s) => s.lang);
  const regionCode = useRegionStore((s) => s.code);
  const t = makeT(lang);
  const [tab, setTab] = useState<Tab>('tb');
  const [bundle, setBundle] = useState<Bundle | null>(null);

  useEffect(() => {
    void (async () => {
      // Entries first, then lines filtered by entry_id — defence in depth:
      // RLS scopes this server-side, but the explicit filter prevents a
      // single misconfigured policy from leaking another tenant's ledger.
      const [a, e] = await Promise.all([
        supabase.from('accounts').select('*').eq('business_id', business.id),
        supabase.from('journal_entries').select('*').eq('business_id', business.id),
      ]);
      const entryIds = (e.data ?? []).map((x) => x.id);
      const l =
        entryIds.length === 0
          ? { data: [] }
          : await supabase.from('journal_lines').select('*').in('entry_id', entryIds);
      setBundle({
        accounts: a.data ?? [],
        entries: e.data ?? [],
        lines: l.data ?? [],
      });
    })();
  }, [business.id]);

  const tb = useMemo(() => bundle && buildTrialBalance(bundle), [bundle]);
  const pnl = useMemo(() => {
    if (!bundle) return null;
    const start = new Date();
    start.setMonth(start.getMonth() - 11);
    start.setDate(1);
    return buildProfitLoss(bundle, start, new Date());
  }, [bundle]);
  const bs = useMemo(() => bundle && buildBalanceSheet(bundle), [bundle]);

  function exportCsv() {
    const ccy = getRegion(regionCode).currencyCode;
    const lines: string[] = [];
    if (tab === 'tb' && tb) {
      lines.push(`Trial Balance,,${ccy}`);
      lines.push('Code,Account,Dr,Cr');
      tb.rows.forEach((r) =>
        lines.push(
          `${q(r.account.gl_code)},${q(r.account.name)},${r.debit > 0 ? r.debit : ''},${
            r.credit > 0 ? r.credit : ''
          }`,
        ),
      );
      lines.push(`,Total,${tb.totalDebit},${tb.totalCredit}`);
      downloadCsv(
        `trial-balance-${business.name}-${new Date().toISOString().slice(0, 10)}.csv`,
        lines.join('\n'),
      );
      return;
    }
    if (tab === 'pnl' && pnl) {
      lines.push(`Profit & Loss,,${ccy}`);
      lines.push('Section,Code,Account,Amount');
      pnl.income.forEach((r) =>
        lines.push(`Income,${q(r.account.gl_code)},${q(r.account.name)},${r.net}`),
      );
      lines.push(`Income,,Total income,${pnl.totalIncome}`);
      pnl.expense.forEach((r) =>
        lines.push(`Expense,${q(r.account.gl_code)},${q(r.account.name)},${r.net}`),
      );
      lines.push(`Expense,,Total expense,${pnl.totalExpense}`);
      lines.push(`,,Net profit,${pnl.netProfit}`);
      downloadCsv(
        `profit-loss-${business.name}-${new Date().toISOString().slice(0, 10)}.csv`,
        lines.join('\n'),
      );
      return;
    }
    if (tab === 'bs' && bs) {
      lines.push(`Balance Sheet,,${ccy}`);
      lines.push('Section,Code,Account,Amount');
      bs.assets.forEach((r) =>
        lines.push(`Assets,${q(r.account.gl_code)},${q(r.account.name)},${r.net}`),
      );
      lines.push(`Assets,,Total assets,${bs.totalAssets}`);
      bs.liabilities.forEach((r) =>
        lines.push(`Liabilities,${q(r.account.gl_code)},${q(r.account.name)},${r.net}`),
      );
      lines.push(`Liabilities,,Total liabilities,${bs.totalLiabilities}`);
      bs.equity.forEach((r) =>
        lines.push(`Equity,${q(r.account.gl_code)},${q(r.account.name)},${r.net}`),
      );
      lines.push(`Equity,,Retained earnings (current period),${bs.retainedEarnings}`);
      lines.push(`Equity,,Total equity,${bs.totalEquity}`);
      lines.push(`,,Total liabilities + equity,${bs.totalLiabilities + bs.totalEquity}`);
      downloadCsv(
        `balance-sheet-${business.name}-${new Date().toISOString().slice(0, 10)}.csv`,
        lines.join('\n'),
      );
      return;
    }
  }

  return (
    <main className="mx-auto max-w-3xl px-4 py-4 md:px-6 md:py-6">
      <div className="mb-4 flex items-center justify-between">
        <h1 className="font-display text-2xl">{t('reports.title')}</h1>
        <Button
          size="sm"
          variant="secondary"
          leftIcon={<Download size={14} />}
          onClick={exportCsv}
          disabled={!bundle}
        >
          CSV
        </Button>
      </div>

      <div className="mb-4 flex gap-2">
        {(
          [
            { id: 'tb', label: t('reports.trial_balance') },
            { id: 'pnl', label: t('reports.pnl') },
            { id: 'bs', label: t('reports.bs') },
          ] as { id: Tab; label: string }[]
        ).map((tt) => (
          <button
            key={tt.id}
            onClick={() => setTab(tt.id)}
            className={`rounded-pill whitespace-nowrap border px-3 py-1.5 text-xs font-medium ${
              tab === tt.id ? 'bg-ink text-paper border-ink' : 'text-muted border-line bg-white'
            }`}
          >
            {tt.label}
          </button>
        ))}
      </div>

      {!bundle ? (
        <p className="text-muted text-sm">Loading…</p>
      ) : tab === 'tb' && tb ? (
        <Card>
          <table className="w-full text-sm">
            <thead className="text-muted text-xs">
              <tr>
                <th className="text-left">Code</th>
                <th className="text-left">Account</th>
                <th className="text-right">Dr</th>
                <th className="text-right">Cr</th>
              </tr>
            </thead>
            <tbody>
              {tb.rows.map((r) => (
                <tr key={r.account.id} className="border-line border-t">
                  <td className="py-2 font-mono">{r.account.gl_code}</td>
                  <td>{r.account.name}</td>
                  <td className="tnum text-right font-mono">
                    {r.debit > 0 ? formatMoney(r.debit, regionCode) : ''}
                  </td>
                  <td className="tnum text-right font-mono">
                    {r.credit > 0 ? formatMoney(r.credit, regionCode) : ''}
                  </td>
                </tr>
              ))}
              <tr className="border-ink border-t-2 font-semibold">
                <td colSpan={2} className="py-2">
                  Total
                </td>
                <td className="tnum text-right font-mono">
                  {formatMoney(tb.totalDebit, regionCode)}
                </td>
                <td className="tnum text-right font-mono">
                  {formatMoney(tb.totalCredit, regionCode)}
                </td>
              </tr>
            </tbody>
          </table>
          <div className="mt-3 flex justify-end">
            <Pill tone={tb.balanced ? 'green' : 'red'}>
              {tb.balanced
                ? 'Balanced ✓'
                : `Off by ${formatMoney(tb.totalDebit - tb.totalCredit, regionCode)}`}
            </Pill>
          </div>
        </Card>
      ) : tab === 'pnl' && pnl ? (
        <Card>
          <h2 className="font-display mb-3 text-lg">Profit & Loss — last 12 months</h2>
          <Section title="Income" rows={pnl.income} total={pnl.totalIncome} positive />
          <Section title="Expenses" rows={pnl.expense} total={pnl.totalExpense} />
          <div className="border-ink font-display mt-4 flex justify-between border-t-2 pt-3 text-lg">
            <span>Net profit</span>
            <span className={`tnum font-mono ${pnl.netProfit >= 0 ? 'text-green' : 'text-red'}`}>
              {formatMoney(pnl.netProfit, regionCode)}
            </span>
          </div>
        </Card>
      ) : tab === 'bs' && bs ? (
        <Card>
          <h2 className="font-display mb-3 text-lg">Balance Sheet — as at today</h2>
          {!bs.isBalanced && (
            <div className="bg-red/10 text-red rounded-card mb-3 p-3 text-sm font-semibold">
              ⚠ Out of balance by {formatMoney(Math.abs(bs.outOfBalanceAmount), regionCode)} —
              assets do not equal liabilities + equity.
            </div>
          )}
          <Section title="Assets" rows={bs.assets} total={bs.totalAssets} positive />
          <Section title="Liabilities" rows={bs.liabilities} total={bs.totalLiabilities} />
          <div className="border-line mt-4 border-t pt-3">
            <p className="mb-2 text-sm font-medium">Equity</p>
            <ul className="mb-2 space-y-1 text-sm">
              {bs.equity.map((r) => (
                <li key={r.account.id} className="flex justify-between">
                  <span className="text-muted">
                    <span className="font-mono">{r.account.gl_code}</span> {r.account.name}
                  </span>
                  <span className="tnum font-mono">{formatMoney(r.net, regionCode)}</span>
                </li>
              ))}
              <li className="text-muted flex justify-between">
                <span>Retained earnings (current period)</span>
                <span className="tnum font-mono">
                  {formatMoney(bs.retainedEarnings, regionCode)}
                </span>
              </li>
            </ul>
            <div className="flex justify-between text-sm font-medium">
              <span>Total equity</span>
              <span className="tnum font-mono">{formatMoney(bs.totalEquity, regionCode)}</span>
            </div>
          </div>
          <div className="border-ink font-display mt-4 flex justify-between border-t-2 pt-3 text-lg">
            <span>Total liabilities + equity</span>
            <span className="tnum font-mono">
              {formatMoney(bs.totalLiabilities + bs.totalEquity, regionCode)}
            </span>
          </div>
        </Card>
      ) : null}
    </main>
  );
}

function Section({
  title,
  rows,
  total,
  positive,
}: {
  title: string;
  rows: { account: { id: string; gl_code: string; name: string }; net: number }[];
  total: number;
  positive?: boolean;
}) {
  const regionCode = useRegionStore((s) => s.code);
  return (
    <div className="mb-3">
      <p className="mb-1 text-sm font-medium">{title}</p>
      <ul className="mb-1 space-y-1 text-sm">
        {rows.length === 0 ? (
          <li className="text-muted text-xs">— no balances —</li>
        ) : (
          rows.map((r) => (
            <li key={r.account.id} className="flex justify-between">
              <span className="text-muted">
                <span className="font-mono">{r.account.gl_code}</span> {r.account.name}
              </span>
              <span className="tnum font-mono">{formatMoney(r.net, regionCode)}</span>
            </li>
          ))
        )}
      </ul>
      <div className="border-line flex justify-between border-t pt-1 text-sm font-medium">
        <span>Total {title.toLowerCase()}</span>
        <span className={`tnum font-mono ${positive ? 'text-green' : ''}`}>
          {formatMoney(total, regionCode)}
        </span>
      </div>
    </div>
  );
}
